import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router';
import { observer } from 'mobx-react-lite';

import { Comment } from '../../app/entities';
import { CommentItem } from './CommentItem';
import { CommentIcon } from '../icons/CommentIcon';
import { breakPoint } from '../../app/theme';
import { EmptyDrawerPlaceholder } from '../empty-drawer.placeholder';
import { CommentContext } from '../../core/contexts/comment-context/comment.context';
import { DrawingContext } from '@producer/ui-kit';

type CommentsListProps = {
  showCompleted?: boolean;
};

export const CommentsList: React.FC<CommentsListProps> = observer(({ showCompleted }) => {
  const {
    playerRef,
    comments,
    selectedComment,
    commentsListRef,
    selectComment,
    setPdfCurrentPage,
  } = useContext(CommentContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const { clearDrawingPath, setIsDrawing } = useContext(DrawingContext);

  const filteredComments = comments.filter((el) =>
    showCompleted ? true : !el.completed,
  ) as Comment[];

  const hasComment = !!comments.length;

  useEffect(() => {
    const currentCommentId = searchParams.get('commentId');

    if (currentCommentId) {
      const comment = Comment.getOne(currentCommentId);

      if (comment) {
        const targetComment = comment?.parent || comment;

        const element = document.getElementById(targetComment._id);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [comments, searchParams, selectedComment]);

  const handleSelectComment = async (comment?: Comment) => {
    if (!comment?._id) {
      selectComment?.(undefined);
    } else {
      const targetComment = comment?.parent || comment;

      if (!targetComment) {
        return;
      }

      selectComment?.(targetComment);

      if (targetComment.timestamp && playerRef?.current) {
        playerRef.current.currentTime = targetComment.timestamp;
        playerRef.current.pause();
      }

      if (targetComment.page || targetComment.page === 0) {
        setPdfCurrentPage?.(targetComment.page);
      }

      if (!targetComment._id || targetComment._id === selectedComment?._id) {
        return;
      }

      clearDrawingPath();
      setIsDrawing(false);

      searchParams.set('commentId', targetComment._id);
      setSearchParams(searchParams);
    }
  };

  return (
    <CommentsContainer ref={commentsListRef}>
      {hasComment ? (
        filteredComments?.map((comment) => (
          <CommentContainer key={comment._id} data-selected={comment._id === selectedComment?._id}>
            <CommentItem
              comment={comment as Comment}
              onClick={(comment) => {
                if (!comment._id || comment._id === selectedComment?._id) {
                  return;
                }

                handleSelectComment(comment);
              }}
            />

            {comment.replies?.map((reply: Comment) => (
              <CommentItem
                key={reply._id}
                comment={reply}
                onClick={(reply) => {
                  if (!reply.parent._id || reply.parent._id === selectedComment?._id) {
                    return;
                  }

                  handleSelectComment(reply.parent);
                }}
                isReply
              />
            ))}
          </CommentContainer>
        ))
      ) : (
        <EmptyDrawerPlaceholder
          icon={<CommentIcon />}
          text={'It is quiet in here. Start the conversation by leaving the first comment'}
        />
      )}
    </CommentsContainer>
  );
});

const CommentsContainer = styled.div`
  border-radius: 0 0 1.6rem 0;
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;
  position: relative;

  @media screen and (max-width: ${breakPoint.medium - 1}px) {
    padding-bottom: 12rem;
  }
`;

const CommentContainer = styled.div`
  border-left: 0.2rem solid;
  border-left-color: transparent;
  padding-inline-end: calc(0.8rem - 100vw + 100%);

  @media screen and (min-width: ${breakPoint.medium}px) {
    padding-inline-end: min(1.2rem - (40rem - 100%), 1.2rem);
  }

  & > :not(:first-child) {
    margin-inline-start: 1.6rem;
  }

  &:hover {
    cursor: pointer;

    &:not([data-selected='true']) {
      background-color: var(--color-grayscale-charleston);
    }
  }

  &[data-selected='true'] {
    border-left-color: var(--color-shades-hibiscus);
  }
`;
